import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Check, 
  X,
  Building2,
  Shield,
  Users,
  BarChart3,
  Brain,
  Lock,
  Globe,
  Layers,
  HeadphonesIcon,
  FileText,
  Zap,
  Sparkles,
  Crown,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MediaKshetra Brand Colors
const GOLD = '#D4A84B';
const GOLD_MUTED = '#B8975F';

export const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const fetchPricingPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Failed to fetch pricing plans:', error);
      // Fallback to hardcoded plans if API fails
      setPlans(getDefaultPlans());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPlans = () => [
    {
      id: 'basic',
      name: 'Basic',
      monthly_price: 150,
      annual_price: 1620,
      description: 'Perfect for small teams getting started with performance marketing infrastructure',
      features: [
        'Up to 3 platform integrations',
        'Core MOI™ scoring',
        'Governance Console (limited flags)',
        'Taxonomy Builder',
        'UTM Builder',
        'Manual data upload',
        'Basic PDF export',
        '5 users',
        '1 workspace'
      ],
      limitations: [
        'No multi-tenant',
        'No SSO',
        'No API access',
        'No advanced compliance calibration'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      monthly_price: 450,
      annual_price: 4860,
      description: 'For growing agencies needing advanced features and multi-workspace support',
      features: [
        'Everything in Basic',
        'Unlimited platform connections',
        'Advanced MOI™ scoring',
        'Full Governance tracking',
        'Compliance rule engine',
        'Multi-workspace support (5)',
        'Advanced PDF + PPT exports',
        'AI Assistant (higher usage cap)',
        '15 users'
      ],
      limitations: [
        'No multi-tenant architecture',
        'No SSO integration',
        'No API access'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthly_price: null,
      annual_price: null,
      description: 'Custom infrastructure for large organizations with complex requirements',
      features: [
        'Everything in Pro',
        'Multi-tenant architecture',
        'Client-level governance dashboards',
        'Custom compliance rule library',
        'SAML SSO (Okta / Azure AD)',
        'API access',
        'Dedicated onboarding',
        'SLA guarantee',
        'Unlimited users',
        'Custom MOI™ calibration'
      ],
      limitations: [],
      annual_only: true,
      popular: false
    }
  ];

  const handleSubscribe = async (planId) => {
    if (planId === 'enterprise') {
      window.location.href = 'mailto:enterprise@mediakshetra.com?subject=GIOS Enterprise Consultation Request';
      return;
    }
    
    // Route to placeholder checkout
    toast.info('Redirecting to checkout...', {
      description: 'Stripe billing is coming soon. This is a demo flow.'
    });
  };

  const getPrice = (plan) => {
    if (plan.monthly_price === null) {
      return { display: 'Custom', subtext: 'Annual contract' };
    }
    
    if (billingCycle === 'annual') {
      const monthlyEquivalent = Math.round(plan.annual_price / 12);
      return {
        display: `$${monthlyEquivalent}`,
        subtext: `/mo billed annually`,
        total: `$${plan.annual_price.toLocaleString()}/year`,
        savings: plan.monthly_price * 12 - plan.annual_price
      };
    }
    
    return {
      display: `$${plan.monthly_price}`,
      subtext: '/month'
    };
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'basic': return Sparkles;
      case 'pro': return Zap;
      case 'enterprise': return Crown;
      default: return Layers;
    }
  };

  const governanceFeatures = [
    'Role-based access control (RBAC)',
    'Complete audit trail logging',
    'Human-in-the-loop approval workflows',
    'Data isolation & workspace security',
    'Compliance-ready architecture',
    'Session-level security controls'
  ];

  const aiCapabilities = [
    'Insight Agent — Performance analysis & trend detection',
    'Creative Agent — Content generation & optimization',
    'Media Agent — Budget orchestration & forecasting',
    'Orchestrator — Multi-agent coordination',
    'Knowledge Graph — Institutional memory',
    'RAG System — Context-aware responses'
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const displayPlans = plans.length > 0 ? plans : getDefaultPlans();

  return (
    <DashboardLayout>
      <div className="p-8 max-w-6xl mx-auto" data-testid="pricing-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded mb-4"
               style={{ backgroundColor: 'rgba(212, 168, 75, 0.1)', border: '1px solid rgba(212, 168, 75, 0.2)' }}>
            <Building2 className="w-3 h-3" style={{ color: GOLD_MUTED }} />
            <span className="text-[10px] tracking-wider uppercase" style={{ color: GOLD_MUTED }}>Premium Pricing</span>
          </div>
          
          <h1 className="text-3xl font-light tracking-tight mb-3">
            Govern. Orchestrate. Scale.
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Choose the Growth Infrastructure tier aligned with your scale ambition.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 p-1.5 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-zinc-700 text-white shadow-sm' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'annual' 
                  ? 'bg-zinc-700 text-white shadow-sm' 
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid="billing-annual"
            >
              Annual
              <Badge 
                className="text-[10px] px-2 py-0.5"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#10B981' }}
              >
                Save 10%
              </Badge>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {displayPlans.map((plan) => {
            const Icon = getPlanIcon(plan.id);
            const isPopular = plan.popular;
            const price = getPrice(plan);
            const isEnterprise = plan.id === 'enterprise';
            
            return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden transition-all hover:border-primary/50 ${
                  isPopular ? 'border-2 shadow-lg shadow-primary/10' : 'border-zinc-800'
                }`}
                style={isPopular ? { borderColor: GOLD } : {}}
                data-testid={`plan-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0">
                    <Badge 
                      className="rounded-none rounded-bl-lg px-3 py-1.5 text-xs font-semibold"
                      style={{ backgroundColor: GOLD, color: '#000' }}
                    >
                      Most Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className={`p-2.5 rounded-lg ${
                        isPopular ? 'bg-primary/20' : 'bg-zinc-800'
                      }`}
                      style={isPopular ? { backgroundColor: 'rgba(212, 168, 75, 0.15)' } : {}}
                    >
                      <Icon 
                        className="w-5 h-5" 
                        style={{ color: isPopular ? GOLD : '#a1a1aa' }}
                      />
                    </div>
                    <CardTitle className="text-xl font-medium">{plan.name}</CardTitle>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {plan.description}
                  </p>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Price */}
                  <div className="pt-2">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-light">{price.display}</span>
                      <span className="text-muted-foreground text-sm">{price.subtext}</span>
                    </div>
                    {price.total && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {price.total}
                      </p>
                    )}
                    {price.savings > 0 && (
                      <p className="text-xs mt-1" style={{ color: '#10B981' }}>
                        Save ${price.savings.toLocaleString()} per year
                      </p>
                    )}
                    {isEnterprise && (
                      <p className="text-xs text-amber-500 mt-2">
                        Annual contract only
                      </p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    className={`w-full h-11 font-medium ${
                      isPopular ? '' : isEnterprise ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-800 hover:bg-zinc-700'
                    }`}
                    style={isPopular ? { backgroundColor: GOLD, color: '#000' } : {}}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {isEnterprise ? 'Request Enterprise Consultation' : `Start ${plan.name}`}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>

                  {/* Features */}
                  <div className="pt-4 border-t border-zinc-800">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
                      What's included
                    </p>
                    <ul className="space-y-2.5">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <Check 
                            className="w-4 h-4 flex-shrink-0 mt-0.5" 
                            style={{ color: isPopular ? GOLD : '#71717a' }}
                          />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Limitations (if any) */}
                  {plan.limitations && plan.limitations.length > 0 && (
                    <div className="pt-4 border-t border-zinc-800/50">
                      <p className="text-xs text-muted-foreground/60 uppercase tracking-wider mb-3">
                        Not included
                      </p>
                      <ul className="space-y-2">
                        {plan.limitations.map((limitation, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-zinc-600" />
                            <span className="text-sm text-zinc-600">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Sections */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Governance */}
          <Card className="p-6 bg-zinc-900/50" style={{ border: '1px solid rgba(212, 168, 75, 0.15)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5" style={{ color: GOLD }} />
              <h3 className="text-base font-medium">Governance & Compliance</h3>
              <Badge variant="outline" className="text-[10px] ml-auto">Pro & Enterprise</Badge>
            </div>
            <ul className="space-y-2">
              {governanceFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* AI Architecture */}
          <Card className="p-6 bg-zinc-900/50" style={{ border: '1px solid rgba(212, 168, 75, 0.15)' }}>
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5" style={{ color: GOLD }} />
              <h3 className="text-base font-medium">AI Agent Architecture</h3>
              <Badge variant="outline" className="text-[10px] ml-auto">All Plans</Badge>
            </div>
            <ul className="space-y-2">
              {aiCapabilities.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: GOLD }} />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* FAQ / Contact */}
        <Card className="p-6 text-center bg-zinc-900/50" style={{ border: '1px solid rgba(212, 168, 75, 0.15)' }}>
          <h3 className="text-lg font-medium mb-2">Need a Custom Solution?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Our team can help you find the right plan for your organization's needs.
          </p>
          <Button 
            variant="outline"
            onClick={() => handleSubscribe('enterprise')}
            style={{ borderColor: GOLD, color: GOLD }}
            data-testid="contact-sales-btn"
          >
            <HeadphonesIcon className="w-4 h-4 mr-2" />
            Talk to Sales
          </Button>
        </Card>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-xs text-muted-foreground/50">
            MediaKshetra GIOS™ — Growth Infrastructure Operating System
          </p>
          <p className="text-xs text-muted-foreground/40 mt-1">
            All prices in USD. Enterprise requires annual commitment.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Pricing;
